import { useEffect, useMemo, useRef, useState } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Sparkles, UserRound } from 'lucide-react';
import { Button, Card, ConfirmDialog, Skeleton, cn, toast } from '@mlm/ui';
import { fillSuggestedQuestion } from '@mlm/shared';
import {
  clearAssistantMemory,
  createAssistantConversation,
  deleteAssistantConversation,
  listAssistantConversations,
  listAssistantMessages,
  listAssistantPersonas,
  regenerateAssistantMessage,
  renameAssistantConversation,
  saveAssistantIntake,
  setAssistantMessageFeedback,
  setAssistantRecall,
  sendAssistantMessage,
  sendAssistantMessageStream,
  type AssistantMessageDto,
} from '../lib/assistant-api';
import { useSpeechOutput } from '../lib/use-speech';
import { useTenantConfig } from '../storefront/lib/hooks';
import { Composer } from '../components/assistant/composer';
import { ConversationList } from '../components/assistant/conversation-list';
import { FallbackHelp } from '../components/assistant/fallback-help';
import { IntakeCard } from '../components/assistant/intake-card';
import { MessageBubble } from '../components/assistant/message-bubble';
import { PersonaPicker } from '../components/assistant/persona-picker';

/**
 * /assistant — the member's AI assistant. First-class page (a tenant may later
 * promote it to the landing surface): thread sidebar ≥lg, single-pane flow on
 * mobile (list → persona picker → thread). Request/response only — no polling;
 * the thread refetches after each exchange. When the feature flag is off (deep
 * link) or a send degrades (`fallback: true`), the scripted FallbackHelp layer
 * takes over — never a dead end.
 */
export function AssistantPage() {
  const qc = useQueryClient();
  const assistantEnabled = useTenantConfig().data?.assistantEnabled ?? false;

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [picking, setPicking] = useState(false);
  const [draft, setDraft] = useState('');
  const [pending, setPending] = useState<string | null>(null);
  // Text streamed so far for the in-flight answer (null = nothing streaming).
  const [streamText, setStreamText] = useState<string | null>(null);
  const [capRemaining, setCapRemaining] = useState<number | null>(null);
  const [sendError, setSendError] = useState<{ error: string; fallback: boolean } | null>(null);
  // "About you" editor open (thread header), and persona ids whose first-run
  // intake card was saved/dismissed this visit (so a partial save doesn't
  // immediately re-show the card).
  const [editingIntake, setEditingIntake] = useState(false);
  const [intakeDismissed, setIntakeDismissed] = useState<Set<string>>(new Set());
  // Pending destructive action awaiting confirmation.
  const [confirming, setConfirming] = useState<
    | { kind: 'delete-thread'; id: string }
    | { kind: 'clear-memory' }
    | { kind: 'disable-recall' }
    | null
  >(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  const tts = useSpeechOutput();

  const personasQ = useQuery({
    queryKey: ['assistant', 'personas'],
    queryFn: listAssistantPersonas,
    enabled: assistantEnabled,
    staleTime: 5 * 60_000,
  });
  const conversationsQ = useQuery({
    queryKey: ['assistant', 'conversations'],
    queryFn: listAssistantConversations,
    enabled: assistantEnabled,
  });
  // Paged newest-first; older pages load on demand (long threads used to lose
  // their head silently at the 50-message mark).
  const messagesQ = useInfiniteQuery({
    queryKey: ['assistant', 'thread', selectedId],
    queryFn: ({ pageParam }) =>
      listAssistantMessages(selectedId!, { limit: THREAD_PAGE_SIZE, cursor: pageParam }),
    enabled: assistantEnabled && Boolean(selectedId),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) =>
      lastPage.messages.length === THREAD_PAGE_SIZE
        ? lastPage.messages.at(-1)?.createdAt
        : undefined,
  });

  // Pages are newest-first (page 0 newest); flatten then reverse for display.
  const messages = useMemo(
    () => (messagesQ.data?.pages ?? []).flatMap((p) => p.messages).slice().reverse(),
    [messagesQ.data],
  );

  const personas = personasQ.data?.personas ?? [];
  const conversations = conversationsQ.data ?? [];
  const activeConversation = conversations.find((c) => c.id === selectedId) ?? null;
  const activePersona = personas.find((p) => p.id === activeConversation?.personaId) ?? null;

  // Today's remaining messages, known on load (sends keep it fresh after that).
  const bootstrapCap = personasQ.data?.capRemaining ?? null;
  useEffect(() => setCapRemaining(bootstrapCap), [bootstrapCap]);

  // Follow the bottom on new/pending turns — but NOT when older pages prepend.
  const newestId = messages.at(-1)?.id ?? null;
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: 'end' });
  }, [newestId, pending, streamText]);

  // Per-thread UI: close the About-you editor and stop any read-aloud.
  useEffect(() => {
    setEditingIntake(false);
    tts.cancel();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  const createConvo = useMutation({
    mutationFn: (personaId: string) => createAssistantConversation(personaId),
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: ['assistant', 'conversations'] });
      setPicking(false);
      setSelectedId(row.id);
      setSendError(null);
    },
    meta: { errorMessage: 'Could not start a chat.' },
  });

  const deleteConvo = useMutation({
    mutationFn: (id: string) => deleteAssistantConversation(id),
    onSuccess: (_d, id) => {
      qc.invalidateQueries({ queryKey: ['assistant', 'conversations'] });
      if (selectedId === id) setSelectedId(null);
    },
    meta: { successMessage: 'Chat deleted.' },
  });

  const clearMemory = useMutation({
    mutationFn: clearAssistantMemory,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['assistant', 'personas'] }),
    meta: { successMessage: 'The assistant forgot its saved notes about you.' },
  });

  const recall = personasQ.data?.recall ?? { tenantEnabled: false, optedOut: false };
  const setRecall = useMutation({
    mutationFn: setAssistantRecall,
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['assistant', 'personas'] });
      toast.success(
        res.recallEnabled
          ? 'The assistant will remember your past chats.'
          : res.clearedDigests > 0
            ? `Turned off — ${res.clearedDigests} chat ${res.clearedDigests === 1 ? 'summary' : 'summaries'} deleted.`
            : 'Turned off — new chats will start fresh.',
      );
    },
    meta: { errorMessage: 'Could not change that — please try again.' },
  });

  const saveIntake = useMutation({
    mutationFn: (vars: { personaId: string; answers?: Record<string, string>; skip?: boolean }) =>
      saveAssistantIntake(vars.personaId, { answers: vars.answers, skip: vars.skip }),
    onSuccess: (_res, vars) => {
      qc.invalidateQueries({ queryKey: ['assistant', 'personas'] });
      setIntakeDismissed((s) => new Set(s).add(vars.personaId));
      setEditingIntake(false);
    },
    meta: { errorMessage: 'Could not save that — please try again.' },
  });

  const send = useMutation({
    // Returns a typed result (never throws) — errors render inline, not as toasts.
    mutationFn: async ({ id, body }: { id: string; body: string }) => {
      const controller = new AbortController();
      abortRef.current = controller;
      let streamed = false;
      const result = await sendAssistantMessageStream(
        id,
        body,
        {
          onDelta: (text) => {
            streamed = true;
            setStreamText((prev) => (prev ?? '') + text);
          },
        },
        controller.signal,
      );
      // Streaming never got off the ground (no proxy support, offline): fall
      // back to the plain request/response send exactly once. Safe only here —
      // nothing was persisted yet, so this can't duplicate the member's turn.
      if (!result.ok && result.error === 'network_error' && !streamed) {
        return sendAssistantMessage(id, body);
      }
      return result;
    },
    onSuccess: async (result) => {
      if (result.ok) {
        setCapRemaining(result.capRemaining);
        setSendError(null);
        setDraft('');
      } else if (result.error !== 'stopped') {
        setSendError({ error: result.error, fallback: result.fallback });
      }
      // The server persists the member's turn BEFORE calling the model and
      // keeps a stopped answer's partial, so refetch on every outcome.
      await qc.invalidateQueries({ queryKey: ['assistant', 'thread', selectedId] });
      qc.invalidateQueries({ queryKey: ['assistant', 'conversations'] });
      setPending(null);
      setStreamText(null);
      abortRef.current = null;
    },
  });

  function sendText(body: string) {
    if (!body || !selectedId || send.isPending) return;
    setPending(body);
    setStreamText(null);
    setSendError(null);
    tts.cancel();
    send.mutate({ id: selectedId, body });
  }

  /** Stop generating — the server keeps whatever it produced. */
  function stopStreaming() {
    abortRef.current?.abort();
  }

  const regenerate = useMutation({
    mutationFn: () => regenerateAssistantMessage(selectedId!),
    onSuccess: async (result) => {
      if (result.ok) {
        setCapRemaining(result.capRemaining);
        setSendError(null);
      } else {
        setSendError({ error: result.error, fallback: result.fallback });
      }
      await qc.invalidateQueries({ queryKey: ['assistant', 'thread', selectedId] });
    },
  });

  const rate = useMutation({
    mutationFn: (vars: { messageId: string; value: 'up' | 'down' | null }) =>
      setAssistantMessageFeedback(vars.messageId, vars.value),
    // Optimistic: a rating should register instantly, and a failed one is not
    // worth interrupting the conversation over.
    onMutate: (vars) => {
      qc.setQueryData(
        ['assistant', 'thread', selectedId],
        (old: { pages: Array<{ messages: AssistantMessageDto[] }> } | undefined) =>
          old && {
            ...old,
            pages: old.pages.map((p) => ({
              ...p,
              messages: p.messages.map((m) =>
                m.id === vars.messageId ? { ...m, feedback: vars.value } : m,
              ),
            })),
          },
      );
    },
    onError: () => qc.invalidateQueries({ queryKey: ['assistant', 'thread', selectedId] }),
  });

  const rename = useMutation({
    mutationFn: (vars: { id: string; title: string }) =>
      renameAssistantConversation(vars.id, vars.title),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['assistant', 'conversations'] }),
    meta: { errorMessage: 'Could not rename that chat.' },
  });

  function submit() {
    sendText(draft.trim());
  }

  // ── Persona intake / suggestions (derived) ────────────────────────────────
  const intakeQuestions = activePersona?.intake ?? [];
  const unansweredIntake = intakeQuestions.filter((q) => !q.answer);
  const showFirstRunIntake =
    Boolean(activePersona) &&
    unansweredIntake.length > 0 &&
    !activePersona!.intakeSkipped &&
    !intakeDismissed.has(activePersona!.id) &&
    messages.length === 0 &&
    !pending;
  const intakeAnswers = Object.fromEntries(
    intakeQuestions.filter((q) => q.answer).map((q) => [q.key, q.answer!]),
  );
  const suggestions = (activePersona?.suggestedQuestions ?? [])
    .map((t) => fillSuggestedQuestion(t, intakeAnswers))
    .filter((s): s is string => Boolean(s))
    .slice(0, 4);

  const intakeCard = activePersona ? (
    <IntakeCard
      key={`${editingIntake ? 'edit' : 'first'}-${activePersona.id}`}
      icon={activePersona.icon}
      personaName={activePersona.name}
      questions={intakeQuestions}
      saving={saveIntake.isPending}
      mode={editingIntake ? 'edit' : 'first-run'}
      onSave={(answers) => saveIntake.mutate({ personaId: activePersona.id, answers })}
      onSkip={() => saveIntake.mutate({ personaId: activePersona.id, skip: true })}
      onCancel={() => setEditingIntake(false)}
    />
  ) : null;

  // Feature off (deep link): scripted help only.
  if (!assistantEnabled) {
    return (
      <div className="mx-auto max-w-2xl space-y-4">
        <PageTitle />
        <FallbackHelp variant="full" />
      </div>
    );
  }

  const listPane = (
    <div className="flex h-full min-h-0 flex-col gap-3">
      <ConversationList
        conversations={conversations}
        personas={personas}
        selectedId={selectedId}
        onSelect={(id) => {
          setSelectedId(id);
          setPicking(false);
          setSendError(null);
        }}
        onNew={() => {
          setPicking(true);
          setSelectedId(null);
        }}
        onDelete={(id) => setConfirming({ kind: 'delete-thread', id })}
        onRename={(id, title) => rename.mutate({ id, title })}
      />
      <div className="flex flex-col gap-2">
        {recall.tenantEnabled && (
          <label className="flex cursor-pointer items-start gap-2 text-[11px] text-muted-foreground">
            <input
              type="checkbox"
              className="mt-0.5 h-3.5 w-3.5 shrink-0 cursor-pointer accent-primary"
              checked={!recall.optedOut}
              disabled={setRecall.isPending}
              onChange={(e) => {
                // Turning it OFF deletes existing summaries, so confirm first.
                if (!e.target.checked) setConfirming({ kind: 'disable-recall' });
                else setRecall.mutate(true);
              }}
            />
            <span>Remember my past chats</span>
          </label>
        )}
        <button
          type="button"
          className="rounded text-left text-[11px] text-muted-foreground underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          onClick={() => setConfirming({ kind: 'clear-memory' })}
        >
          Clear what the assistant remembers about me
        </button>
      </div>
    </div>
  );

  const pickerPane = (
    <div className="space-y-4">
      <div>
        <h2 className="text-base font-semibold">Who do you want to talk to?</h2>
        <p className="text-sm text-muted-foreground">
          Pick an assistant — you can start as many chats as you like.
        </p>
      </div>
      {personasQ.isLoading ? (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-32 w-full" />
          ))}
        </div>
      ) : (
        <PersonaPicker
          personas={personas}
          disabled={createConvo.isPending}
          onPick={(personaId) => createConvo.mutate(personaId)}
        />
      )}
    </div>
  );

  const threadPane = selectedId ? (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex items-center gap-2 border-b pb-3">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setSelectedId(null)}
          className="lg:hidden"
          aria-label="Back to chats"
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <span className="text-xl" aria-hidden>
          {activePersona?.icon ?? '✨'}
        </span>
        <div className="min-w-0">
          <p className="truncate text-sm font-medium">{activePersona?.name ?? 'Assistant'}</p>
          <p className="truncate text-[11px] text-muted-foreground">
            {activePersona?.tagline ?? ''}
          </p>
        </div>
        {intakeQuestions.length > 0 && (
          <Button
            variant="ghost"
            size="sm"
            className="ml-auto shrink-0 text-xs"
            onClick={() => setEditingIntake((v) => !v)}
          >
            <UserRound className="mr-1 h-3.5 w-3.5" /> About you
          </Button>
        )}
      </div>

      <div
        className="min-h-0 flex-1 space-y-2 overflow-y-auto py-4"
        role="log"
        aria-live="polite"
        aria-label="Conversation"
      >
        {messagesQ.hasNextPage && (
          <div className="flex justify-center pb-2">
            <Button
              variant="ghost"
              size="sm"
              className="text-xs"
              disabled={messagesQ.isFetchingNextPage}
              onClick={() => messagesQ.fetchNextPage()}
            >
              {messagesQ.isFetchingNextPage ? 'Loading…' : 'Load older messages'}
            </Button>
          </div>
        )}
        {editingIntake && (messages.length > 0 || pending) && (
          <div className="pb-2">{intakeCard}</div>
        )}
        {messagesQ.isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-10 w-48" />
            <Skeleton className="ml-auto h-10 w-40" />
          </div>
        ) : messages.length === 0 && !pending ? (
          showFirstRunIntake || editingIntake ? (
            <div className="flex h-full flex-col justify-center">{intakeCard}</div>
          ) : (
            <div className="flex h-full flex-col items-center justify-center gap-3 text-center text-muted-foreground">
              <span className="text-3xl">{activePersona?.icon ?? '✨'}</span>
              <p className="text-sm">
                {activePersona?.accountAware
                  ? 'Ask about your earnings, team, orders — or anything else.'
                  : `Chat with the ${activePersona?.name ?? 'assistant'}.`}
              </p>
              {suggestions.length > 0 && (
                <div className="flex flex-wrap justify-center gap-2 px-2">
                  {suggestions.map((s) => (
                    <button
                      key={s}
                      type="button"
                      disabled={send.isPending}
                      className="rounded-full border bg-background px-3 py-1.5 text-left text-xs text-foreground transition-colors hover:border-o hover:text-o"
                      onClick={() => sendText(s)}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )
        ) : (
          messages.map((m: AssistantMessageDto) => (
            <MessageBubble
              key={m.id}
              message={m}
              speakingId={tts.speakingId}
              onSpeak={(id, text) => tts.speak(id, text, activePersona?.voiceHint ?? undefined)}
              onStopSpeak={tts.cancel}
              ttsSupported={tts.supported}
              isLast={m.id === newestId}
              busy={send.isPending || regenerate.isPending}
              onFeedback={(value) => rate.mutate({ messageId: m.id, value })}
              onRegenerate={() => {
                tts.cancel();
                regenerate.mutate();
              }}
            />
          ))
        )}
        {pending && (
          <>
            <div className="flex justify-end">
              <div className="max-w-[85%] whitespace-pre-wrap break-words rounded-2xl rounded-br-sm bg-o px-3 py-2 text-sm text-white opacity-80">
                {pending}
              </div>
            </div>
            {streamText ? (
              <MessageBubble
                message={{
                  id: 'streaming',
                  role: 'assistant',
                  body: streamText,
                  metadata: {},
                  createdAt: '',
                }}
                streaming
                speakingId={null}
                onSpeak={() => {}}
                onStopSpeak={tts.cancel}
                ttsSupported={false}
              />
            ) : (
              <div className="flex justify-start">
                <div
                  className="flex items-center gap-1.5 rounded-2xl rounded-bl-sm bg-muted px-3 py-2.5"
                  aria-label="Assistant is typing"
                >
                  <ThinkingDot delay="0ms" />
                  <ThinkingDot delay="150ms" />
                  <ThinkingDot delay="300ms" />
                </div>
              </div>
            )}
          </>
        )}
        {sendError && (
          <div className="space-y-3">
            <div
              role="alert"
              className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-destructive"
            >
              {sendErrorText(sendError.error)}
            </div>
            {sendError.fallback && <FallbackHelp variant="inline" />}
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      <Composer
        draft={draft}
        onDraftChange={setDraft}
        onSubmit={submit}
        onStop={stopStreaming}
        sending={send.isPending}
        capRemaining={capRemaining}
      />
    </div>
  ) : null;

  return (
    <div className="flex h-[calc(100dvh-12rem)] flex-col gap-3">
      <PageTitle />
      <div className="grid min-h-0 flex-1 gap-4 lg:grid-cols-[280px_1fr]">
        {/* Sidebar: always on ≥lg; on mobile it IS the page when no thread/picker is open. */}
        <Card className={cn('min-h-0 p-3', (selectedId || picking) && 'hidden lg:block')}>
          {listPane}
        </Card>
        <Card
          className={cn(
            'min-h-0 p-3',
            !selectedId && !picking && 'hidden lg:block',
          )}
        >
          {picking || !selectedId ? (
            <div className="h-full overflow-y-auto">
              {picking ? (
                <div className="space-y-3">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setPicking(false)}
                    className="lg:hidden"
                  >
                    <ArrowLeft className="mr-1 h-4 w-4" /> Back
                  </Button>
                  {pickerPane}
                </div>
              ) : (
                pickerPane
              )}
            </div>
          ) : (
            threadPane
          )}
        </Card>
      </div>

      <ConfirmDialog
        open={confirming !== null}
        onOpenChange={(open) => !open && setConfirming(null)}
        title={
          confirming?.kind === 'clear-memory'
            ? 'Clear saved notes?'
            : confirming?.kind === 'disable-recall'
              ? 'Stop remembering past chats?'
              : 'Delete this chat?'
        }
        description={
          confirming?.kind === 'clear-memory'
            ? 'The assistant will forget everything it saved about you, including your profile answers. Your chats stay.'
            : confirming?.kind === 'disable-recall'
              ? 'Summaries of your earlier chats will be deleted and new chats will start fresh. Your chats themselves stay — you can turn this back on any time.'
              : 'Its messages are gone for good.'
        }
        confirmLabel={
          confirming?.kind === 'clear-memory'
            ? 'Clear notes'
            : confirming?.kind === 'disable-recall'
              ? 'Turn off'
              : 'Delete chat'
        }
        confirmVariant="destructive"
        loading={clearMemory.isPending || deleteConvo.isPending || setRecall.isPending}
        onConfirm={async () => {
          if (!confirming) return;
          if (confirming.kind === 'clear-memory') await clearMemory.mutateAsync();
          else if (confirming.kind === 'disable-recall') await setRecall.mutateAsync(false);
          else await deleteConvo.mutateAsync(confirming.id);
          setConfirming(null);
        }}
      />
    </div>
  );
}

function PageTitle() {
  return (
    <div className="flex items-center gap-2">
      <Sparkles className="h-5 w-5 text-o" />
      <h1 className="text-xl font-semibold">Assistant</h1>
    </div>
  );
}

const THREAD_PAGE_SIZE = 30;

function ThinkingDot({ delay }: { delay: string }) {
  return (
    <span
      className="h-1.5 w-1.5 animate-bounce rounded-full bg-muted-foreground/60"
      style={{ animationDelay: delay }}
    />
  );
}

function sendErrorText(code: string): string {
  switch (code) {
    case 'daily_cap_reached':
      return "You've used up today's assistant messages. They reset tomorrow!";
    case 'budget_exhausted':
      return 'The assistant is resting for the rest of the month (usage limit reached).';
    case 'assistant_not_configured':
    case 'assistant_upstream_error':
      return "The assistant couldn't answer right now. Please try again in a moment.";
    case 'network_error':
      return 'Connection problem — check your internet and try again.';
    case 'stream_incomplete':
      return 'The answer was cut off mid-sentence. Ask again to get the rest.';
    default:
      return 'Something went wrong. Please try again.';
  }
}
